// src/modals/PricingModeToggleModal.jsx — Phase 6.C PR #167d (ADR-102)
//
// Modal dédiée bascule mode pricing TYPE B (manual) ↔ TYPE A (pricelabs).
//
// 2 variantes UX selon `currentMode` :
//   - manual → pricelabs : warning rouge + saisie PriceLabs Listing ID
//   - pricelabs → manual : warning bleu + checkbox marketplace_active=false bloquante
//
// Wording Q-1 final (BPRd-3) : PAS de fausse promesse de "PRÉSERVÉS". Explicite
// que PriceLabs prend la suite et que les overrides manuels seront remplacés
// ~6h après bascule (lors du prochain sync Beds24).
//
// Q-2 / BPRd-4 : pré-check Beds24 marketplace via
// pricing_marketplace_check_beds24 avant submit. Si status='detected_inactive',
// affiche warning bloquant (skip explicite via checkbox d'override).
//
// LPRd-5 : tooltip warning si logement a >50 dates manual_push_set actives
// (signal "production réelle" pour alerter user).
//
// MPRd-2 : exclusivité avec autres modaux (Beds24SyncPanel gère togglingConfig
// state distinct de editing/editingMinStay).

import { useEffect, useMemo, useState, useRef } from "react";
import { AlertTriangle, Shuffle, CheckCircle, Info, Loader2 } from "lucide-react";
import { DS } from "../lib/ui";
import { supabase } from "../supabase";

const DIGITS_ONLY_REGEX = /^\d+$/;

export default function PricingModeToggleModal({
  existing,
  manualPushSetCount, // LPRd-5 : count daily_pricing.source='manual_push_set' actives (optionnel)
  onClose,
  onSubmit, // async (payload, opts) → { ok, status, error?, data? }
}) {
  // Defensive : si existing absent on rend null après les hooks (rules-of-hooks)
  const currentMode = existing?.pricing_mode || "manual"; // 'manual' | 'pricelabs'
  const targetMode = currentMode === "manual" ? "pricelabs" : "manual";
  const isToTypeA = targetMode === "pricelabs"; // TYPE B → TYPE A

  const [pricelabsListingId, setPricelabsListingId] = useState(
    existing?.pricelabs_listing_id || "",
  );
  const [pricelabsUrl, setPricelabsUrl] = useState(
    existing?.pricelabs_url || "",
  );
  // pour A→B : checkbox confirmation marketplace désactivé côté Beds24
  const [marketplaceConfirmed, setMarketplaceConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);

  // Q-2 / BPRd-4 : pré-check Beds24 marketplace (sens B → A)
  const [marketplaceCheck, setMarketplaceCheck] = useState(null); // null | {status, detail?, error?}
  const [marketplaceCheckLoading, setMarketplaceCheckLoading] = useState(false);
  // Override admin si le check Beds24 retourne 'detected_inactive' mais
  // l'admin sait que c'est connecté (rare edge case).
  const [overrideBeds24Check, setOverrideBeds24Check] = useState(false);
  const abortRef = useRef(null);

  // Pré-check Beds24 marketplace au mount (sens B → A uniquement)
  useEffect(() => {
    if (!isToTypeA || !existing?.logement_id) return;
    const controller = new AbortController();
    abortRef.current = controller;
    (async () => {
      setMarketplaceCheckLoading(true);
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.access_token) {
          setMarketplaceCheck({
            status: "beds24_error",
            error: "not_authenticated",
          });
          return;
        }
        const qs = new URLSearchParams({
          action: "pricing_marketplace_check_beds24",
          logement_id: existing?.logement_id || "",
        });
        const res = await fetch(`/api/beds24-mapping?${qs.toString()}`, {
          signal: controller.signal,
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (controller.signal.aborted) return;
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          setMarketplaceCheck({
            status: "beds24_error",
            error: json.error || `http_${res.status}`,
          });
          return;
        }
        setMarketplaceCheck({
          status: json.status,
          detail: json.detail,
          error: json.error,
        });
      } catch (e) {
        if (e?.name === "AbortError") return;
        setMarketplaceCheck({
          status: "beds24_error",
          error: String(e?.message ?? e).slice(0, 200),
        });
      } finally {
        if (!controller.signal.aborted) setMarketplaceCheckLoading(false);
      }
    })();
    return () => {
      controller.abort();
    };
  }, [isToTypeA, existing?.logement_id]);

  // Esc keyboard handler (calque PricingConfigEditModal)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Auto-fill pricelabs_url si vide quand listing_id saisi (parité PricingConfigEditModal LPR3-3)
  function setListingId(value) {
    setPricelabsListingId(value);
    if (value && DIGITS_ONLY_REGEX.test(String(value)) && !pricelabsUrl) {
      setPricelabsUrl(`https://app.pricelabs.co/pricing?listings=${value}`);
    }
  }

  // Validation form selon sens
  const validation = useMemo(() => {
    if (isToTypeA) {
      // B → A : listing_id requis (digits)
      if (
        !pricelabsListingId ||
        !DIGITS_ONLY_REGEX.test(String(pricelabsListingId))
      ) {
        return {
          ok: false,
          field: "pricelabs_listing_id",
          message: "PriceLabs Listing ID requis (digits uniquement)",
        };
      }
      // Q-2 : si marketplace check retourne detected_inactive, exige override
      if (
        marketplaceCheck?.status === "detected_inactive" &&
        !overrideBeds24Check
      ) {
        return {
          ok: false,
          field: "beds24_marketplace",
          message:
            "PriceLabs ne semble pas connecté côté Beds24 marketplace. Cocher l'override si tu es sûr·e.",
        };
      }
      return { ok: true };
    }
    // A → B : checkbox marketplace_confirmed obligatoire
    if (!marketplaceConfirmed) {
      return {
        ok: false,
        field: "marketplace_confirmed",
        message: "Confirmer d'abord que marketplace est désactivé côté Beds24.",
      };
    }
    return { ok: true };
  }, [
    isToTypeA,
    pricelabsListingId,
    marketplaceConfirmed,
    marketplaceCheck,
    overrideBeds24Check,
  ]);

  const isValid = validation.ok && !marketplaceCheckLoading;
  const hasManualPushSetWarning =
    typeof manualPushSetCount === "number" && manualPushSetCount > 50;

  // Defensive : si existing absent post-mount (cas marginal navigateur),
  // rend null après tous les hooks (rules-of-hooks respect).
  if (!existing) return null;

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (!isValid || submitting) return;
    setSubmitting(true);
    setServerError(null);

    // Build PATCH payload
    const payload = {
      logement_id: existing.logement_id,
      pricing_mode: targetMode,
      _existing: true, // flag pour use-pricing-config-list.upsert → PATCH
    };
    if (isToTypeA) {
      payload.pricelabs_listing_id = pricelabsListingId || null;
      payload.pricelabs_url = pricelabsUrl || null;
      // marketplace_active reste false par défaut PR #167d (admin l'active
      // séparément si besoin). On NE le set PAS automatiquement à true.
      payload.marketplace_active = existing.marketplace_active === true;
      // Q-2 override (BPRd-4)
      if (overrideBeds24Check) {
        payload.skip_beds24_marketplace_check = true;
      }
    } else {
      // A → B : forcer marketplace_active=false (le user vient de confirmer)
      payload.marketplace_active = false;
    }

    const opts = {};
    if (existing.updated_at) opts.ifMatch = existing.updated_at;

    const result = await onSubmit(payload, opts);
    setSubmitting(false);
    if (!result?.ok && !result?.conflict) {
      setServerError(result?.error || "Erreur serveur");
    }
    // Si OK ou conflict → parent ferme la modal
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.5)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        fontFamily: DS.fontBody,
      }}
    >
      <div
        className="fbz-modal-sheet"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: DS.card,
          borderRadius: 12,
          maxWidth: 560,
          width: "100%",
          maxHeight: "92vh",
          overflowY: "auto",
          padding: 22,
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: 14 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              fontFamily: DS.fontDisplay,
              fontSize: 18,
              fontWeight: 600,
              color: isToTypeA ? DS.danger : DS.marine,
            }}
          >
            {isToTypeA ? <AlertTriangle size={18} strokeWidth={2.2} /> : <Shuffle size={18} strokeWidth={2.2} />}
            <span>{isToTypeA ? "Activer PriceLabs" : "Repasser en mode manuel"}
            {" sur "}
            <code style={codeStyle}>{existing.logement_id}</code></span>
          </div>
          <div style={{ fontSize: 11, color: DS.textMuted, marginTop: 4 }}>
            Mode actuel :{" "}
            {currentMode === "manual" ? "Manual" : "PriceLabs"}
            {" · Cible : "}
            {targetMode === "manual" ? "Manual" : "PriceLabs"}
          </div>
        </div>

        {/* Wording Q-1 final (BPRd-3) — explicite, pas de fausse "PRÉSERVÉS" */}
        {isToTypeA ? (
          <div style={warningBoxRed}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              PriceLabs va prendre le contrôle complet du pricing :
            </div>
            <ul style={{ paddingLeft: 20, margin: 0, lineHeight: 1.6 }}>
              <li>Les prix seront définis automatiquement par PriceLabs</li>
              <li>
                Tes overrides manuels actuels seront remplacés (~6h après
                bascule, lors du prochain sync Beds24)
              </li>
              <li>Tu pourras toujours utiliser les blocages de dates</li>
            </ul>
          </div>
        ) : (
          <div style={warningBoxBlue}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              Tu reprends le contrôle manuel via FBZ :
            </div>
            <ul style={{ paddingLeft: 20, margin: 0, lineHeight: 1.6 }}>
              <li>
                Les prix Beds24 actuels seront chargés en base au prochain seed
                / sync 6h
              </li>
              <li>FBZ pourra pousser des prix manuels par date (Niveau 3)</li>
              <li>
                <strong>Important</strong> : d'abord désactiver la connexion
                PriceLabs ↔ Beds24 marketplace côté Beds24, puis confirmer
                ci-dessous
              </li>
            </ul>
          </div>
        )}

        {/* LPRd-5 : warning si >50 dates manual_push_set actives */}
        {hasManualPushSetWarning && (
          <div style={{ ...warningBoxAmber, display: "flex", gap: 7, alignItems: "flex-start" }}>
            <AlertTriangle size={13} strokeWidth={2.3} style={{ flexShrink: 0, marginTop: 1 }} /><span>Ce logement a actuellement <strong>{manualPushSetCount}</strong>{" "}
            dates avec prix manuels actifs (source manual_push_set). Vérifie que
            tu veux vraiment basculer.</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* TYPE B → TYPE A : champs PriceLabs */}
          {isToTypeA && (
            <>
              <div style={fieldRow}>
                <label style={labelStyle}>PriceLabs Listing ID *</label>
                <input
                  value={pricelabsListingId}
                  onChange={(e) => setListingId(e.target.value)}
                  placeholder="ex: 85595087785"
                  style={inputStyle}
                />
                {validation.field === "pricelabs_listing_id" && (
                  <div style={errorStyle}>{validation.message}</div>
                )}
              </div>
              <div style={fieldRow}>
                <label style={labelStyle}>PriceLabs URL</label>
                <input
                  value={pricelabsUrl}
                  onChange={(e) => setPricelabsUrl(e.target.value)}
                  placeholder="auto-générée si vide"
                  style={inputStyle}
                />
                <div style={{ fontSize: 10, color: DS.textMuted, marginTop: 3 }}>
                  Auto-remplie si vide à la saisie du Listing ID. Non écrasée si
                  saisie manuelle.
                </div>
              </div>

              {/* Q-2 / BPRd-4 : affichage état marketplace check Beds24 */}
              <div style={fieldRow}>
                <label style={labelStyle}>
                  Vérification Beds24 marketplace
                </label>
                {marketplaceCheckLoading ? (
                  <div style={{ ...marketplaceBoxNeutral, display: "flex", gap: 7, alignItems: "center" }}>
                    <Loader2 size={13} /> Vérification Beds24 marketplace…
                  </div>
                ) : marketplaceCheck ? (
                  marketplaceCheck.status === "detected_active" ? (
                    <div style={{ ...marketplaceBoxOk, display: "flex", gap: 7, alignItems: "flex-start" }}>
                      <CheckCircle size={13} strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 1 }} /><span>PriceLabs détecté côté Beds24 (statut référencé).</span>
                    </div>
                  ) : marketplaceCheck.status === "detected_inactive" ? (
                    <div style={marketplaceBoxWarn}>
                      <div style={{ display: "flex", alignItems: "center", gap: 5, fontWeight: 700, marginBottom: 4 }}>
                        <AlertTriangle size={13} strokeWidth={2.3} /> PriceLabs PAS détecté côté Beds24
                      </div>
                      <div style={{ fontSize: 11, lineHeight: 1.5 }}>
                        Active d'abord la connexion PriceLabs ↔ Beds24
                        marketplace côté Beds24 admin, puis recharge cette
                        modal. Sinon, coche l'override ci-dessous si tu es
                        certain·e.
                      </div>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginTop: 8,
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={overrideBeds24Check}
                          onChange={(e) =>
                            setOverrideBeds24Check(e.target.checked)
                          }
                        />
                        Override : forcer la bascule (je sais ce que je fais)
                      </label>
                    </div>
                  ) : (
                    <div style={{ ...marketplaceBoxNeutral, display: "flex", gap: 7, alignItems: "flex-start" }}>
                      <Info size={13} strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 1 }} /><span>Statut Beds24 indéterminé ({marketplaceCheck.status}).
                      Continue en confiance, l'audit est tracé.</span>
                    </div>
                  )
                ) : (
                  <div style={marketplaceBoxNeutral}>—</div>
                )}
                {validation.field === "beds24_marketplace" && (
                  <div style={errorStyle}>{validation.message}</div>
                )}
              </div>
            </>
          )}

          {/* TYPE A → TYPE B : checkbox confirmation marketplace désactivé */}
          {!isToTypeA && (
            <div style={fieldRow}>
              <label
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={marketplaceConfirmed}
                  onChange={(e) => setMarketplaceConfirmed(e.target.checked)}
                  style={{ marginTop: 2 }}
                />
                <span>
                  J'ai désactivé la connexion PriceLabs ↔ Beds24 marketplace
                  côté Beds24 admin. *
                </span>
              </label>
              {validation.field === "marketplace_confirmed" && (
                <div style={{ ...errorStyle, marginTop: 6 }}>
                  {validation.message}
                </div>
              )}
            </div>
          )}

          {serverError && (
            <div
              style={{
                ...errorStyle,
                display: "flex",
                gap: 7,
                alignItems: "flex-start",
                padding: 8,
                background: DS.dangerBg,
                borderRadius: 6,
                marginTop: 8,
              }}
            >
              <AlertTriangle size={13} strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 1 }} /><span>{serverError}</span>
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              marginTop: 18,
            }}
          >
            <button type="button" className="fbz-press" onClick={onClose} style={btnSecondary}>
              Annuler
            </button>
            <button
              type="submit"
              disabled={!isValid || submitting}
              className="fbz-press"
              style={{
                ...(isToTypeA ? btnPrimaryDanger : btnPrimary),
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                opacity: !isValid || submitting ? 0.5 : 1,
                cursor: !isValid || submitting ? "not-allowed" : "pointer",
              }}
            >
              {submitting
                ? <><Loader2 size={13} /> Bascule en cours…</>
                : isToTypeA
                  ? "Activer PriceLabs"
                  : "Repasser en manuel"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Styles inline (parité PricingConfigEditModal) ────────────────────

const fieldRow = { marginBottom: 12 };
const labelStyle = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  color: DS.navy,
  marginBottom: 4,
  textTransform: "uppercase",
  letterSpacing: 0.4,
};
const inputStyle = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: DS.radiusControl,
  border: `0.5px solid ${DS.borderHover}`,
  fontSize: 13,
  fontFamily: "inherit",
  boxSizing: "border-box",
  background: DS.card,
};
const errorStyle = {
  color: DS.dangerText,
  fontSize: 11,
  marginTop: 3,
};
const btnPrimary = {
  background: DS.corail,
  color: "#fff",
  border: "none",
  borderRadius: DS.radiusControl,
  padding: "8px 16px",
  fontSize: 12,
  fontWeight: 700,
  fontFamily: "inherit",
  cursor: "pointer",
};
const btnPrimaryDanger = {
  ...btnPrimary,
  background: DS.danger,
};
const btnSecondary = {
  background: DS.card,
  color: DS.marine,
  border: `0.5px solid ${DS.borderHover}`,
  borderRadius: DS.radiusControl,
  padding: "8px 16px",
  fontSize: 12,
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: "pointer",
};

const warningBoxBase = {
  padding: "12px 14px",
  borderRadius: 8,
  border: "1px solid",
  fontSize: 12,
  lineHeight: 1.5,
  marginBottom: 14,
};
const warningBoxRed = {
  ...warningBoxBase,
  background: DS.dangerBg,
  borderColor: `${DS.danger}44`,
  color: DS.dangerText,
};
const warningBoxBlue = {
  ...warningBoxBase,
  background: DS.cielSoft,
  borderColor: `${DS.ciel}55`,
  color: DS.marineDeep,
};
const warningBoxAmber = {
  ...warningBoxBase,
  background: DS.warningBg,
  borderColor: DS.warning,
  color: DS.warningText,
  fontSize: 11,
  fontWeight: 600,
};

const marketplaceBoxBase = {
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid",
  fontFamily: "inherit",
};
const marketplaceBoxOk = {
  ...marketplaceBoxBase,
  background: DS.successBg,
  borderColor: `${DS.success}55`,
  color: DS.successText,
  fontSize: 12,
};
const marketplaceBoxWarn = {
  ...marketplaceBoxBase,
  background: DS.warningBg,
  borderColor: DS.warning,
  color: DS.warningText,
};
const marketplaceBoxNeutral = {
  ...marketplaceBoxBase,
  background: DS.pageBg,
  borderColor: DS.border,
  color: DS.textSecondary,
  fontSize: 12,
};

const codeStyle = {
  background: "rgba(255,255,255,0.4)",
  padding: "1px 6px",
  borderRadius: 4,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: 13,
};
